'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode } from 'react';
import { Select as SelectPrimitive } from '@base-ui/react/select';
import { CaretDown, Check } from '@phosphor-icons/react';
export type SelectOption = {
  value: string;
  label: string;
};
type Size = 'sm' | 'md';
type Props = {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Optional inline prefix shown in the trigger before the selected label (e.g. "Sort:"). */
  prefix?: ReactNode;
  /** Placeholder when nothing is selected. */
  placeholder?: string;
  /** Accessible label — required since the trigger has no visible label by default. */
  ariaLabel: string;
  size?: Size;
  disabled?: boolean;
  className?: string;
};
const SIZE: Record<Size, string> = {
  sm: 'h-[var(--field-height-sm)] px-3.5 text-base gap-1.5',
  md: 'h-[var(--field-height)] px-4 text-base gap-2'
};
const ICON_SIZE: Record<Size, number> = {
  sm: 12,
  md: 14
};
export function Select(t0) {
  const $ = _c(34);
  const {
    value,
    onChange,
    options,
    prefix,
    placeholder,
    ariaLabel,
    size: t1,
    disabled: t2,
    className: t3
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const disabled = t2 === undefined ? false : t2;
  const className = t3 === undefined ? "" : t3;
  let t4;
  if ($[0] !== onChange) {
    t4 = v => typeof v === "string" && onChange(v);
    $[0] = onChange;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = SIZE[size];
  let t6;
  if ($[2] !== className || $[3] !== t5) {
    t6 = ["inline-flex items-center justify-between rounded-[var(--radius-field)] border border-border bg-background text-foreground font-medium", "transition-colors hover:border-foreground/40", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary", "data-[popup-open]:border-primary data-[popup-open]:ring-2 data-[popup-open]:ring-primary/20", "disabled:opacity-50 disabled:cursor-not-allowed", t5, className].filter(Boolean);
    $[2] = className;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = t6.join(" ");
  let t8;
  if ($[5] !== prefix) {
    t8 = prefix && <span className="text-muted-foreground shrink-0">{prefix}</span>;
    $[5] = prefix;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== options || $[8] !== placeholder) {
    t9 = currentValue => options.find(o => o.value === currentValue)?.label ?? currentValue ?? placeholder;
    $[7] = options;
    $[8] = placeholder;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== placeholder || $[11] !== t9) {
    t10 = <SelectPrimitive.Value placeholder={placeholder} className="truncate">{t9}</SelectPrimitive.Value>;
    $[10] = placeholder;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t8) {
    t11 = <span className="flex items-center gap-1.5 min-w-0">{t8}{t10}</span>;
    $[13] = t10;
    $[14] = t8;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = ICON_SIZE[size];
  let t13;
  if ($[16] !== t12) {
    t13 = <SelectPrimitive.Icon className="text-muted-foreground shrink-0"><CaretDown size={t12} weight="bold" /></SelectPrimitive.Icon>;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== ariaLabel || $[19] !== t11 || $[20] !== t13 || $[21] !== t7) {
    t14 = <SelectPrimitive.Trigger aria-label={ariaLabel} className={t7}>{t11}{t13}</SelectPrimitive.Trigger>;
    $[18] = ariaLabel;
    $[19] = t11;
    $[20] = t13;
    $[21] = t7;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = ["min-w-[var(--anchor-width)] max-h-72 overflow-y-auto", "rounded-xl border border-border bg-popover text-popover-foreground shadow-lg", "p-1.5", "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0", "transition-opacity duration-150 ease-out outline-none"];
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const t16 = t15.join(" ");
  let t17;
  if ($[24] !== options) {
    t17 = options.map(_temp);
    $[24] = options;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t17) {
    t18 = <SelectPrimitive.Portal><SelectPrimitive.Positioner sideOffset={6} alignItemWithTrigger={false} className="z-50 outline-none"><SelectPrimitive.Popup className={t16}><SelectPrimitive.List>{t17}</SelectPrimitive.List></SelectPrimitive.Popup></SelectPrimitive.Positioner></SelectPrimitive.Portal>;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== disabled || $[29] !== t14 || $[30] !== t18 || $[31] !== t4 || $[32] !== value) {
    t19 = <SelectPrimitive.Root value={value} onValueChange={t4} disabled={disabled}>{t14}{t18}</SelectPrimitive.Root>;
    $[28] = disabled;
    $[29] = t14;
    $[30] = t18;
    $[31] = t4;
    $[32] = value;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  return t19;
}
function _temp(opt) {
  return <SelectPrimitive.Item key={opt.value} value={opt.value} className={["flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-lg text-sm cursor-pointer outline-none", "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground", "data-[selected]:font-medium"].join(" ")}><SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText><SelectPrimitive.ItemIndicator className="text-primary"><Check size={14} weight="bold" /></SelectPrimitive.ItemIndicator></SelectPrimitive.Item>;
}